import { observable, action, runInAction } from "mobx";
import { announcementRecords, sameProfileId, resolveMvrAnnouncement } from "../helper/mvrAnnouncement";

const unwrap = response => {
    const data = response && (response.data || response);
    return data && (data.response || data);
};

// Separate from employer onboarding/payment state. No reads on mount or render.
export default class MvrAnnouncementStore {
    @observable saving = observable.map();
    @observable refreshing = observable.map();
    @observable refreshErrors = observable.map();
    pending = new Map();
    refreshPending = new Map();
    session = 0;

    constructor(profileStore, api) {
        this.profiles = profileStore;
        this.apiSource = api;
    }

    get api() {
        return typeof this.apiSource === "function" ? this.apiSource() : this.apiSource;
    }

    @action reset() {
        this.session += 1;
        this.pending.clear();
        this.refreshPending.clear();
        this.saving.clear();
        this.refreshing.clear();
        this.refreshErrors.clear();
    }

    records() {
        return announcementRecords([
            ...(this.profiles.profiles || []),
            this.profiles.currentProfile,
            this.profiles.profile
        ]);
    }

    @action apply(profileId, payload) {
        this.records().forEach(record => {
            if (sameProfileId(record.id, profileId)) {
                // Preserve the first server timestamp; never generate one in the browser.
                const timestamp = record.mvrAnnouncementRead === true && record.mvrAnnouncementReadAt
                    ? record.mvrAnnouncementReadAt : payload.mvrAnnouncementReadAt;
                Object.assign(record, { mvrAnnouncementRead: true, mvrAnnouncementReadAt: timestamp });
            }
        });
    }

    @action acknowledge(context) {
        const key = String(context.profileId);
        const live = resolveMvrAnnouncement(this.profiles);
        if (!live || !sameProfileId(live.profileId, context.profileId) ||
            live.mvrAnnouncementRead !== false) return Promise.reject(new Error("The acting profile has changed. Please try again."));
        if (this.pending.has(key)) return this.pending.get(key);
        const session = this.session;
        const actor = this.profiles.currentProfile && this.profiles.currentProfile.userId;
        this.saving.set(key, true);
        const request = Promise.resolve()
            .then(() => {
                if (session !== this.session || actor !== this.profiles.currentProfile?.userId ||
                    !sameProfileId(context.profileId, this.profiles.currentProfile?.id)) {
                    throw new Error("Your profile changed before acknowledgement started.");
                }
                return this.api.HgSuperPay.acknowledgeMvrAnnouncement(context.profileId);
            })
            .then(response => {
                const payload = unwrap(response);
                if (!payload || !sameProfileId(payload.profileId, context.profileId) ||
                    payload.mvrAnnouncementRead !== true ||
                    typeof payload.mvrAnnouncementReadAt !== "string" ||
                    !Number.isFinite(Date.parse(payload.mvrAnnouncementReadAt)) ||
                    payload.success === false || Number(payload.status) >= 400 || payload.errors) {
                    throw new Error(payload && (payload.errors || payload.message || payload.error) || "Could not save your acknowledgement. Please try again.");
                }
                if (session === this.session && actor === this.profiles.currentProfile?.userId) {
                    this.apply(context.profileId, payload);
                }
                return payload;
            })
            .finally(action(() => {
                if (session === this.session) {
                    this.pending.delete(key);
                    this.saving.delete(key);
                }
            }));
        this.pending.set(key, request);
        return request;
    }

    @action refresh(context, isCurrent = () => true) {
        const key = String(context.profileId);
        if (!isCurrent() || !sameProfileId(context.profileId, this.profiles.currentProfile?.id)) return Promise.resolve();
        if (this.refreshPending.has(key)) return this.refreshPending.get(key);
        const session = this.session;
        const actor = this.profiles.currentProfile && this.profiles.currentProfile.userId;
        const valid = () => session === this.session && isCurrent() &&
            actor === this.profiles.currentProfile?.userId &&
            sameProfileId(context.profileId, this.profiles.currentProfile?.id);
        this.refreshing.set(key, true);
        this.refreshErrors.delete(key);
        const request = Promise.resolve().then(() => {
            if (!valid()) return null;
            return this.api.Profile.loadProfiles();
        }).then(response => {
            if (!valid()) return;
            const profiles = unwrap(response);
            if (!profiles || typeof profiles.forEach !== "function") throw new Error("Invalid profile response");
            const confirmed = announcementRecords(profiles).find(record =>
                sameProfileId(record.id, context.profileId) &&
                record.mvrAnnouncementRead === true &&
                typeof record.mvrAnnouncementReadAt === "string" &&
                Number.isFinite(Date.parse(record.mvrAnnouncementReadAt)));
            if (!confirmed) throw new Error("Profile acknowledgement has not refreshed yet.");
            // Merge only the acknowledged profile's announcement fields. Never
            // reselect profiles, replace edits, or acknowledge other profiles.
            this.apply(context.profileId, confirmed);
        }).catch(error => {
            if (valid()) runInAction(() => this.refreshErrors.set(key,
                "Your acknowledgement was saved, but profile refresh failed."));
        }).finally(action(() => {
            if (session === this.session) {
                this.refreshPending.delete(key);
                this.refreshing.delete(key);
            }
        }));
        this.refreshPending.set(key, request);
        return request;
    }
}
